import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';

const FOCUSABLE = 'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), a[href]';

/** Modal shell like ConfirmDialog, with room for a form or a summary. Esc and the backdrop cancel. */
function Modal({ title, icon, tone = 'primary', busy, onCancel, initialFocusRef, children, footer }) {
  const titleId = useId();
  const ref = useRef(null);

  useEffect(() => {
    const returnTo = document.activeElement;
    (initialFocusRef?.current ?? ref.current?.querySelector(FOCUSABLE))?.focus();
    return () => returnTo?.focus?.();
  }, [initialFocusRef]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = [...ref.current.querySelectorAll(FOCUSABLE)];
    if (!nodes.length) return;
    const [first, last] = [nodes[0], nodes[nodes.length - 1]];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.35)]" onClick={busy ? undefined : onCancel} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="relative w-full max-w-lg bg-surface-container-lowest rounded-2xl border border-outline-variant/30 shadow-[0_20px_25px_-5px_rgba(15,23,42,0.12),0_8px_10px_-6px_rgba(15,23,42,0.06)] p-6"
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              tone === 'danger' ? 'bg-error-container/60 text-error' : 'bg-secondary-container/30 text-secondary'
            }`}
          >
            <Icon name={icon} size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-base font-bold text-on-surface">
              {title}
            </h2>
            <div className="mt-2">{children}</div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

/** Summary of what will be published, then PUT status approved. */
export function ApproveDialog({ scanId, amount, summary, priceNote, busy, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  return (
    <Modal
      title={`Approve scan #${scanId} and publish?`}
      icon="verified"
      busy={busy}
      onCancel={onCancel}
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button icon={busy ? undefined : 'check'} onClick={onConfirm} disabled={busy}>
            {busy ? (
              <>
                <Icon name="progress_activity" size={16} className="animate-spin" />
                Approving…
              </>
            ) : (
              `Approve${amount ? ` (${amount})` : ''}`
            )}
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {summary.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-on-surface-variant">{label}</dt>
            <dd className="font-data-mono text-on-surface text-right break-all">{value}</dd>
          </div>
        ))}
      </dl>
      <p className={`mt-3 flex items-start gap-1.5 text-xs ${priceNote.applies ? 'text-secondary' : 'text-status-pending-text'}`}>
        <Icon name={priceNote.applies ? 'published_with_changes' : 'info'} size={16} className="shrink-0" />
        {priceNote.text}
      </p>
    </Modal>
  );
}

export const REJECT_REASONS = ['Receipt not readable', 'Wrong station', 'Duplicate receipt', 'Receipt too old', 'Not a fuel receipt'];
const MAX_REASON = 255;

/** Required reason (quick picks or free text), then PUT status rejected. */
export function RejectDialog({ scanId, busy, initialReason = '', onConfirm, onCancel }) {
  const [reason, setReason] = useState(initialReason);
  const [error, setError] = useState(null);
  const textRef = useRef(null);

  const submit = () => {
    const value = reason.trim();
    if (!value) {
      setError('Give a reason for the user.');
      textRef.current?.focus();
      return;
    }
    if (value.length > MAX_REASON) {
      setError(`Use at most ${MAX_REASON} characters.`);
      textRef.current?.focus();
      return;
    }
    onConfirm(value);
  };

  return (
    <Modal
      title={`Reject scan #${scanId}?`}
      icon="block"
      tone="danger"
      busy={busy}
      onCancel={onCancel}
      initialFocusRef={textRef}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="dangerSolid" icon={busy ? undefined : 'close'} onClick={submit} disabled={busy}>
            {busy ? (
              <>
                <Icon name="progress_activity" size={16} className="animate-spin" />
                Rejecting…
              </>
            ) : (
              'Reject Scan'
            )}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-on-surface-variant mb-3">The user sees this reason in the app.</p>
        <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Common reasons">
          {REJECT_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={reason === r}
              onClick={() => {
                setReason(r);
                setError(null);
              }}
              className={`h-7 px-2.5 rounded-full text-xs font-semibold border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot ${
                reason === r ? 'bg-primary text-on-primary border-primary' : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant/50 hover:bg-surface-container'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex items-baseline justify-between mb-1">
          <label htmlFor="reject-reason" className="text-xs font-bold text-on-surface">
            Reason <span className="text-error">*</span>
          </label>
          <span className={`font-data-mono text-[10px] ${reason.length > MAX_REASON ? 'text-error' : 'text-on-surface-variant'}`}>
            {reason.length}/{MAX_REASON}
          </span>
        </div>
        <textarea
          ref={textRef}
          id="reject-reason"
          rows={3}
          value={reason}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError(e.target.value.trim() ? null : 'Give a reason for the user.');
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'reject-reason-error' : undefined}
          className={`w-full px-3 py-2 rounded-lg text-sm text-on-surface border outline-none transition resize-y ${
            error ? 'bg-status-alert-bg/40 border-error' : 'bg-surface-container-low border-outline-variant/30 focus:border-primary-container'
          }`}
        />
        <FieldError id="reject-reason-error">{error}</FieldError>
      </form>
    </Modal>
  );
}
